import type { MouseEventHandler } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import eases from 'styles/tools/eases'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import clsx from 'clsx'
import zIndex from 'styles/tools/z-index'
import { without } from 'lib/array/arrayUtils'
import type { SnackbarKind } from 'LuxKit/Snackbar/Snackbar'
import Snackbar from 'LuxKit/Snackbar/Snackbar'

const StickyWrapper = styled.div`
  position: fixed;
  inset: 0;
  z-index: ${zIndex.notification};
  pointer-events: none;

  ${mediaQueryUp.tablet} {
    position: sticky;
    top: calc(${props => props.theme.layout.headerFloatingOffset} + ${props => props.theme.layout.headerRelativeOffset});
  }
`

const PositionedSnackbar = styled.div`
  --snackbar-inline-end-offset: 200%;

  transition: transform 0.3s ${eases.power2.in}, opacity 0.3s ${eases.circ.in};
  transform: translateY(300%);
  pointer-events: none;
  opacity: 0;
  width: 100%;

  ${mediaQueryUp.tablet} {
    width: auto;
    max-width: ${rem(500)};
    min-width: ${rem(360)};
    transform: translateX(var(--snackbar-inline-end-offset));
  }

  [dir="rtl"] & {
    --snackbar-inline-end-offset: -200%;
  }

  &.open {
    transform: translateX(0%) translateY(0%);
    pointer-events: auto;
    transition: transform 0.3s ${eases.power2.out}, opacity 0.3s ${eases.circ.out};
    opacity: 1;
  }
`

const AppSnackbarWrapper = styled.div`
  display: flex;
  inset-inline: 0;
  bottom: 0;
  padding: ${rem(8)};
  position: absolute;
  overflow: hidden;
  ${mediaQueryUp.tablet} {
    justify-content: flex-end;
    padding: ${rem(16)};
    top: 0;
    bottom: unset;
    /* Extra room for shadow */
    padding-bottom: ${rem(80)};
  }
`

const CustomSnackbarContent = styled.div`
  width: 100%;
`

interface SnackbarActionOptions extends Utils.PropsWithDataAttributes {
  label: string
  onAction?: React.MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>
  href?: string
  to?: string
  target?: React.HTMLAttributeAnchorTarget
}

interface StandardSnackbarOptions {
  heading?: string | undefined
  action?: SnackbarActionOptions | undefined
  icon?: React.ReactNode | App.Image | undefined
  renderCustomContent?: undefined
}

interface CustomSnackbarOptions {
  heading?: undefined
  action?: undefined
  icon?: undefined
  /**
   * Rare escape hatch for feature-owned snackbar content. Custom content owns its
   * own presentation and cannot be combined with standard snackbar options.
   */
  renderCustomContent: SnackbarContent
}

interface SnackbarContentProps {
  onClose: () => void
}

type SnackbarContent = (props: SnackbarContentProps) => React.ReactNode
type SnackbarOptions = StandardSnackbarOptions | CustomSnackbarOptions

// Internal config type, do not export.
// Show snackbar is only only external way to interact with this element
interface AppSnackbarConfig {
  description: string
  kind: SnackbarKind
  heading?: string
  icon?: React.ReactNode | App.Image
  action?: SnackbarActionOptions
  renderCustomContent?: SnackbarContent
}

export function showSnackbar(description: string, kind: SnackbarKind, options: SnackbarOptions = {}) {
  document.dispatchEvent(new CustomEvent<AppSnackbarConfig>('app-snackbar', {
    detail: {
      description,
      kind,
      ...options,
    },
  }))
}

/**
 * Renders snackbars in the appropriate place for the appropriate time that are queued with a snackbar
 *
 * Features include:
 * Will animate between snackbars as they are queued
 * Consistent timers based on content of snackbar
 * Hovering over snackbar will pause timer
 * Closes snackbar once a user interacts with it (when it has an action to interact with)
 */
function AppSnackbar() {
  // the queue for all snackbars that should be shown
  const [snackbarQueue, updateSnackbarQueue] = useState<Array<AppSnackbarConfig>>([])
  // The snackbar config we're currently showing
  const [visibleConfig, setVisibleConfig] = useState<AppSnackbarConfig>()
  // whether or not the snackbar should be open
  const [open, setOpen] = useState<boolean>(false)
  // data about the current running snackbar, allows us to pause/resume and cancel timers
  const timeoutRef = useRef<{ id?: number, timestamp?: number, remaining?: number }>({})

  useEffect(() => {
    const onSnackbarEnqueue = (e: CustomEvent<AppSnackbarConfig>) => {
      // snackbar just came in, close existing one if we had one open
      window.clearTimeout(timeoutRef.current.id)
      setOpen(false)
      updateSnackbarQueue((oldQueue) => [...oldQueue, e.detail])
    }

    document.addEventListener('app-snackbar', onSnackbarEnqueue as any)

    return () => {
      document.removeEventListener('app-snackbar', onSnackbarEnqueue as any)
    }
  }, [])

  useEffect(() => {
    if (!visibleConfig) {
      const nextSnackbar = snackbarQueue[0]

      if (nextSnackbar) {
        setVisibleConfig(nextSnackbar)
        updateSnackbarQueue(without(snackbarQueue, nextSnackbar))
        setOpen(true)
      }
    }
  }, [snackbarQueue, visibleConfig])

  const element = useRef<HTMLDivElement>(null)
  const onTransitionEnd = useCallback((e: React.TransitionEvent<HTMLDivElement>) => {
    if (e.propertyName === 'transform' && e.target === element.current) {
      if (!open) {
        // we're closing it and animation has finished
        // so it must no longer be visible, remove it.
        setVisibleConfig(undefined)
      } else if (visibleConfig) {
        window.clearTimeout(timeoutRef.current.id)
        const duration = (visibleConfig.action || visibleConfig.renderCustomContent) ? 8000 : 4000
        // just finished opening, start the timeout to close
        timeoutRef.current.id = window.setTimeout(() => setOpen(false), duration)
        timeoutRef.current.timestamp = new Date().getTime()
        timeoutRef.current.remaining = duration
      }
    }
  }, [open, visibleConfig])

  const closeVisibleSnackbar = useCallback(() => {
    window.clearTimeout(timeoutRef.current.id)
    timeoutRef.current.id = undefined
    setOpen(false)
  }, [])

  const onSnackbarClick = useCallback<MouseEventHandler<HTMLDivElement>>(() => {
    closeVisibleSnackbar()
  }, [closeVisibleSnackbar])

  const onSnackbarActionClick = useCallback<MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>>((e) => {
    e.stopPropagation()
    // make sure we close the snackbar once a user has interacted with it
    closeVisibleSnackbar()
    visibleConfig?.action?.onAction?.(e)
  }, [closeVisibleSnackbar, visibleConfig?.action])

  const pauseTimer = useCallback(() => {
    window.clearTimeout(timeoutRef.current.id)
    const duration = new Date().getTime() - (timeoutRef.current.timestamp ?? 0)
    // update remaining time with how much time has passed
    timeoutRef.current.remaining = (timeoutRef.current.remaining ?? 0) - duration
  }, [])

  const resumeTimer = useCallback(() => {
    if (timeoutRef.current.id) {
      timeoutRef.current.id = window.setTimeout(() => setOpen(false), timeoutRef.current.remaining)
      timeoutRef.current.timestamp = new Date().getTime()
      // remaining hasn't changed, no need to change it
    }
  }, [])

  return (
    <StickyWrapper>
      <AppSnackbarWrapper>
        <PositionedSnackbar
          ref={element}
          onTransitionEnd={onTransitionEnd}
          className={clsx({ open })}
        >
          {visibleConfig?.renderCustomContent && <CustomSnackbarContent
            onMouseEnter={pauseTimer}
            onMouseLeave={resumeTimer}
          >
            {visibleConfig.renderCustomContent({ onClose: closeVisibleSnackbar })}
          </CustomSnackbarContent>}
          {visibleConfig && !visibleConfig.renderCustomContent && <Snackbar
            heading={visibleConfig.heading}
            kind={visibleConfig.kind}
            description={visibleConfig.description}
            action={visibleConfig.action?.label}
            onAction={onSnackbarActionClick}
            actionHref={visibleConfig.action?.href}
            actionTo={visibleConfig.action?.to}
            actionTarget={visibleConfig.action?.target}
            /** @todo let all data attrs go in */
            actionTestId={visibleConfig.action?.['data-testid']}
            icon={visibleConfig.icon}
            onClick={onSnackbarClick}
            onMouseEnter={pauseTimer}
            onMouseLeave={resumeTimer}
          />}
        </PositionedSnackbar>
      </AppSnackbarWrapper>
    </StickyWrapper>
  )
}

export default React.memo(AppSnackbar)
